import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal, Stack, Typography } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { EventsContext } from 'context/events';
import { LeavesContext } from 'context/leaves';

import { Loading } from 'components/common/Loading';
import { EventsVideo } from 'components/events/EventsVideo';
import { useAspenSocket } from 'hooks/useAspenSocket';
import { findFirstEvent } from 'modules/activityState';
import { getDeviceLeafById } from 'modules/leaves';

import { ActivityState } from 'types/aspen/events';
import { AspenSocketMsg } from 'types/aspen/socketMessages';
import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './eventsModal.module.scss';

type ModalEventData = {
  event: ActivityState;
  leaf: AppDeviceLeaf | null;
  previousEventId?: ActivityState['id'];
  nextEventId?: ActivityState['id'];
};

export const EventsModal = () => {
  const { eventId } = useSearch({ from: '/events' });
  const navigate = useNavigate();
  const [modalEventData, setModalEventData] = useState<ModalEventData | null>(null);

  const { leafStore, loading: leavesLoading } = useContext(LeavesContext);
  const { eventsStore, loading: eventsLoading, modalOpen } = useContext(EventsContext);
  const loading = leavesLoading || eventsLoading;
  const events = eventsStore.read().states;

  const { subscribe, unsubscribe, getStates } = useAspenSocket();

  useEffect(() => {
    subscribe((messageEvent: MessageEvent) => {
      const message: AspenSocketMsg = JSON.parse(messageEvent.data);

      if (message.type === 'get_states_resp') {
        if (message.payload) {
          const event = findFirstEvent({
            getStatesResponse: message.payload,
          });

          if (event) {
            const leaves = leafStore.read();
            const leaf = getDeviceLeafById(leaves || {}, event.deviceId);
            setModalEventData({
              event,
              leaf,
            });
          }
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (eventId && !loading) {
      const eventIndex = events.findIndex((e) => e.id === eventId);

      if (eventIndex === -1 && !modalEventData) {
        getStates([
          {
            stateIds: [eventId],
          },
        ]);
      } else {
        const leaves = leafStore.read();
        const currentEvent = events[eventIndex] || modalEventData?.event;

        const modalData: ModalEventData = {
          event: currentEvent,
          leaf: getDeviceLeafById(leaves || {}, currentEvent?.deviceId),
          previousEventId: events[eventIndex - 1]?.id,
          nextEventId: events[eventIndex + 1]?.id,
        };

        // If the event is already set, do not update
        if (modalEventData?.event.id !== modalData.event.id || !modalEventData.leaf) {
          setModalEventData(modalData);
        }
      }
    }
  }, [eventId, loading]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Modal className={styles.modal} open={modalOpen} disableEscapeKeyDown>
      {modalEventData?.event ? (
        <EventsVideo
          event={modalEventData.event}
          leaf={modalEventData.leaf}
          previousEventId={modalEventData.previousEventId}
          nextEventId={modalEventData.nextEventId}
        />
      ) : (
        <Stack className={styles.noEvents} spacing={2}>
          <Typography variant="h5" fontWeight="bold">
            Event not found
          </Typography>
          <Button
            aria-label="apply filters"
            data-testid="button-events-filter-apply"
            variant="contained"
            onClick={() => {
              navigate({
                to: '/events',
                replace: true, // Replace the current history entry
              });
            }}
          >
            Close
          </Button>
        </Stack>
      )}
    </Modal>
  );
};
